import { useEffect, useState } from 'react';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';

/**
 * Persistiert den in der Massenverarbeitung gewählten Abarbeitungsschritt in
 * localStorage – analog zu den Filtern der Vorgangslisten (useVorgangFilters).
 * Dadurch bleibt der Schritt-Filter erhalten, wenn man einen Vorgang öffnet und
 * anschließend zur Massenverarbeitung zurückkehrt.
 */
const STORAGE_KEY = 'massenverarbeitung-aktiver-schritt';

const isVorgangProzessSchritt = (
  value: string,
): value is VorgangProzessSchritt =>
  (Object.values(VorgangProzessSchritt) as string[]).includes(value);

const hydrate = (): VorgangProzessSchritt | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && isVorgangProzessSchritt(stored) ? stored : null;
  } catch {
    // Storage nicht verfügbar (z. B. Safari Private Mode): kein Filter.
    return null;
  }
};

export const useMassenverarbeitungSchritt = () => {
  const [aktiverSchritt, setAktiverSchritt] =
    useState<VorgangProzessSchritt | null>(() => hydrate());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (aktiverSchritt) {
        window.localStorage.setItem(STORAGE_KEY, aktiverSchritt);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Silently fail if storage is unavailable (e.g. Safari private mode).
    }
  }, [aktiverSchritt]);

  return [aktiverSchritt, setAktiverSchritt] as const;
};
